/**
 * Affiliate context: holds affiliate status (pending | active | suspended | rejected)
 * so we can show pending-approval UI and avoid treating 403 "pending approval" as session expiry.
 */
import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';

const AFFILIATE_STATUS_KEY = 'affiliateStatus';

export type AffiliateStatus = 'pending' | 'active' | 'suspended' | 'rejected' | null;

interface AffiliateContextValue {
  affiliateStatus: AffiliateStatus;
  setAffiliateStatus: (status: AffiliateStatus) => void;
  isLoadingStatus: boolean;
  isPending: boolean;
  isActive: boolean;
}

const AffiliateContext = createContext<AffiliateContextValue | undefined>(undefined);

function readStoredStatus(): AffiliateStatus {
  try {
    const stored = sessionStorage.getItem(AFFILIATE_STATUS_KEY);
    if (stored === 'pending' || stored === 'active' || stored === 'suspended' || stored === 'rejected') {
      return stored;
    }
  } catch {
    // ignore
  }
  return null;
}

function writeStoredStatus(status: AffiliateStatus): void {
  try {
    if (status) {
      sessionStorage.setItem(AFFILIATE_STATUS_KEY, status);
    } else {
      sessionStorage.removeItem(AFFILIATE_STATUS_KEY);
    }
  } catch {
    // ignore
  }
}

/**
 * Returns true if the API error is the "pending approval" 403 so we don't clear token / redirect.
 */
export function isPendingApprovalError(status: number, message?: string): boolean {
  if (status !== 403) return false;
  const text = (message || '').toLowerCase();
  return text.includes('pending') && (text.includes('approval') || text.includes('approve'));
}

export const AffiliateProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [affiliateStatus, setState] = useState<AffiliateStatus>(() => readStoredStatus());
  const [isLoadingStatus, setIsLoadingStatus] = useState(true);

  const setAffiliateStatus = useCallback((status: AffiliateStatus) => {
    setState(status);
    writeStoredStatus(status);
  }, []);

  useEffect(() => {
    // Rehydrate from sessionStorage on mount (in case of refresh)
    const stored = readStoredStatus();
    if (stored) {
      setState(stored);
    }
    setIsLoadingStatus(false);
  }, []);

  const value: AffiliateContextValue = {
    affiliateStatus,
    setAffiliateStatus,
    isLoadingStatus,
    isPending: affiliateStatus === 'pending',
    isActive: affiliateStatus === 'active',
  };

  return (
    <AffiliateContext.Provider value={value}>
      {children}
    </AffiliateContext.Provider>
  );
};

export function useAffiliate(): AffiliateContextValue {
  const context = useContext(AffiliateContext);
  if (context === undefined) {
    throw new Error('useAffiliate must be used within an AffiliateProvider');
  }
  return context;
}

/**
 * Safe hook for use inside affiliate pages; returns null if outside provider (e.g. login page).
 */
export function useAffiliateOptional(): AffiliateContextValue | null {
  return useContext(AffiliateContext) ?? null;
}
